import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TimerIcon from '@mui/icons-material/Timer'
import TodayIcon from '@mui/icons-material/Today'

import { ConsultationStatus } from '@/core/models/consultation.model'

import useInterval from '@/hooks/useInterval'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

dayjs.extend(duration)

type MeetingClockComponentProps = {
  startDate: string
  consultationStatus: ConsultationStatus
}

export default function MeetingClockComponent(props: MeetingClockComponentProps) {
  const { startDate, consultationStatus } = props
  const { t } = useTranslation()
  const isTooSmall = useIsMobile()

  const [currentTime, setCurrentTime] = useState(dayjs())

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 60 * 1000)

  const timer = useMemo(() => {
    if (!dayjs(startDate).isValid() || dayjs(startDate).isBefore(currentTime)) return ''

    return dayjs.duration(Math.abs(dayjs(startDate).diff(currentTime))).format('HH[h]mm')
  }, [currentTime, startDate])

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={{ xs: 0, md: 1 }}>
        <Tooltip title={t('header.clock.startingTime.tooltip')}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={isTooSmall ? "start" : "center"} spacing={2}>
            <Stack spacing={0.5} direction="row">
              <AccessTimeIcon />
              <Typography sx={[{ display: 'flex', alignItems: 'center', fontSize: '14px' }]}>{dayjs(startDate).format('HH[h]mm')}</Typography>
            </Stack>
            <Stack spacing={0.5} direction="row">
              <TodayIcon />
              <Typography sx={[{ display: 'flex', alignItems: 'center', fontSize: '14px' }]}>{dayjs(startDate).format('DD/MM/YYYY')}</Typography>
            </Stack>
          </Stack>
        </Tooltip>
        {consultationStatus !== ConsultationStatus.Active && !!timer && (
          <Tooltip title={t('header.clock.duration.tooltip')}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={{ xs: 0, md: 1 }}>
              <TimerIcon />
              <Typography sx={[{ display: 'flex', alignItems: 'center', fontSize: '14px' }]}>{timer}</Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    </>
  )
}
